import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Loading from 'components/Loading';
import COLOR from 'utils/constants/color';
import { handleListLastSemActiveClass, handleBringDataFromSection, handleCheckClassSyncStatus, handleUndoBringDataFromSection } from 'store/assignment/thunks';
import { semeterDateByYear } from 'utils/methods/math';
import * as Sentry from "@sentry/react";
import { useCurrentClass } from 'store/classes/selectors';
import { Undo } from '@mui/icons-material';

const ModalContent = styled.div`
    align-items: center;
    background-color: ${COLOR.white};
    border-radius: 3px;
    display: inline-flex;
    flex-direction: column;
    left: 50%;
    max-width: 500px;
    padding: 16px;
    position: absolute;
    text-align: left;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
`;

const Header = styled.div`
    align-items: center;
    border-bottom: 1px dashed ${COLOR.black800};
    color: ${COLOR.black200};
    display: flex;
    flex-direction: row;
    font-size: 14px;
    font-weight: 600;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 8px;
    width: 100%;
`;

const ModalRow = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin-top: 16px;
    width: 100%;
`;

const Button = styled.button`
    background-color: transparent;
    border: none;
    color: ${COLOR.green200};
    cursor: pointer;
    outline: none;
    transition: all 300ms ease;
    padding: 8px 16px;
    border-radius: 3px;

    &:hover {
        color: ${COLOR.green}
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const Select = styled.select`
    width: 100%;
    padding: 8px;
    font-size: 14px;
    border: 1px solid ${COLOR.black100};
    border-radius: 3px;
    background-color: ${COLOR.white};
    
    &:focus {
        outline: none;
        border-color: ${COLOR.green100};
    }
`;

const SyncedClassesDisplay = React.memo(({ syncedClasses, onUndoClick }) => {
    const uniqueSyncedClasses = useMemo(() => {
        return syncedClasses.reduce((acc, syncedClass) => {
            const classId = syncedClass.fromClassId;
            const existing = acc.get(classId);
            
            if (!existing) {
                acc.set(classId, syncedClass);
            } else {
                const existingDate = existing.syncedAt ? new Date(existing.syncedAt) : null;
                const currentDate = syncedClass.syncedAt ? new Date(syncedClass.syncedAt) : null;
                
                if (currentDate && (!existingDate || currentDate > existingDate)) {
                    acc.set(classId, syncedClass);
                }
            }
            
            return acc;
        }, new Map());
    }, [syncedClasses]);
    
    const uniqueClassesArray = Array.from(uniqueSyncedClasses.values());
    
    if (uniqueClassesArray.length === 0) {
        return null;
    }
    
    return (
        <div style={{ marginBottom: '16px', width: '100%', padding: '12px', backgroundColor: COLOR.green50, borderRadius: '4px' }}>
            <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Already Synced From:</span>
                {onUndoClick && (
                    <Button
                        onClick={onUndoClick}
                        style={{ 
                            fontSize: '12px', 
                            padding: '4px 8px',
                            color: COLOR.red || '#d32f2f',
                            textDecoration: 'underline',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                    >
                     <Undo style={{ fontSize: '12px' }}/>  Undo
                    </Button>
                )}
            </div>
            {uniqueClassesArray.map((syncedClass, index) => (
                <div key={syncedClass.fromClassId || index} style={{ fontSize: '12px', marginBottom: '4px' }}>
                    • {syncedClass.fromClassName || syncedClass.fromClassId} 
                    {syncedClass.syncedAt && ` (${new Date(syncedClass.syncedAt).toLocaleDateString()})`}
                </div>
            ))}
        </div>
    );
});

const BringDataFromSectionModal = ({
    open,
    onClose,
    onSuccess,
    loading
}) => {
    const dispatch = useDispatch();
    const [teacherClasses, setTeacherClasses] = useState([]);
    const [selectedSourceClass, setSelectedSourceClass] = useState(null);
    const [bringDataLoading, setBringDataLoading] = useState(false);
    const [loadingClasses, setLoadingClasses] = useState(false);
    const [syncStatus, setSyncStatus] = useState({ isSynced: false, syncedClasses: [] });
    const [undoDialogOpen, setUndoDialogOpen] = useState(false);
    const [undoLoading, setUndoLoading] = useState(false);
    const currentClass = useCurrentClass();
    useEffect(() => {
        if (open && currentClass?.id) {
            loadClasses();
            loadSyncStatus();
        } else {
            setTeacherClasses([]);
            setSelectedSourceClass(null);
            setSyncStatus({ isSynced: false, syncedClasses: [] });
        }
    }, [open, currentClass?.id]);

    const loadSyncStatus = async () => {
        try {
            const status = await dispatch(handleCheckClassSyncStatus(currentClass?.id));
            if (status) {
                setSyncStatus(status);
            }
        } catch (error) {
            console.error('Error in loadSyncStatus: ', error);
        }
    };

    const loadClasses = async () => {
        setLoadingClasses(true);
        try {
            const classes = await dispatch(handleListLastSemActiveClass(currentClass?.id));
            if (Array.isArray(classes)) {
                const sortedClasses = classes
                    .sort((a, b) => (b.no_of_student_have_submission || 0) - (a.no_of_student_have_submission || 0))
                    .slice(0, 1)
                    .map(item => {
                        const classInfo = typeof item.classInfo === 'string' ? JSON.parse(item.classInfo) : item.classInfo;
                        return {
                            id: classInfo?.id,
                            displayName: classInfo?.displayName || classInfo?.name,
                            no_of_student_have_submission: item.no_of_student_have_submission
                        };
                    });
                setTeacherClasses(sortedClasses);
                if(sortedClasses.length>0){
                    setSelectedSourceClass(sortedClasses[0].id);
                }
            }
        } catch (error) {
            console.error('Error in loadClasses: ', error);
            Sentry.captureException(`Error in loadClasses: ${error}`);
        } finally {
            setLoadingClasses(false);
        }
    };

    const handleBringData = async () => {
        if (!selectedSourceClass || !currentClass?.id) return;
        
        setBringDataLoading(true);
        try {
            await dispatch(handleBringDataFromSection({
                fromClassId: selectedSourceClass,
                toClassId: currentClass.id
            }));
            
            onSuccess(true, "Data brought successfully");
            onClose();
            setSelectedSourceClass(null);
            await loadSyncStatus();
        } catch (error) {
            console.error('Error in handleBringData: ', error);
            Sentry.captureException(`Error in handleBringData: ${error}`);
            onSuccess(false, "Error bringing data from section");
        } finally {
            setBringDataLoading(false);
        }
    };

    const handleUndoClick = () => {
        setUndoDialogOpen(true);
    };

    const handleUndoConfirm = async () => {
        if (!currentClass?.id) return;
        
        setUndoLoading(true);
        try {
            await dispatch(handleUndoBringDataFromSection({
                toClassId: currentClass.id
            }));
            
            setUndoDialogOpen(false);
            onSuccess(true, "Data undo successfully");
            await loadSyncStatus();
        } catch (error) {
            console.error('Error in handleUndoConfirm: ', error);
            Sentry.captureException(`Error in handleUndoConfirm: ${error}`);
            onSuccess(false, "Error undoing data");
        } finally {
            setUndoLoading(false);
        }
    };

    const handleUndoCancel = () => {
        setUndoDialogOpen(false);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
        >
            <ModalContent>
                <Header>
                    Bring Semester 1 Data from Other Section
                    {loading && <Loading size={15} />}
                </Header>
                <div style={{ 
                    marginBottom: '16px', 
                    padding: '10px', 
                    backgroundColor: COLOR.black900, 
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: COLOR.black300,
                    fontStyle: 'italic'
                }}>
                    Note: This will bring Semester 1 data to Semester 1 in this class.
                </div>
                {teacherClasses.length > 0 && teacherClasses[0].no_of_student_have_submission > 0 && (
                    <div style={{ 
                        display: 'block', 
                        marginBottom: '16px', 
                        padding: '12px', 
                        backgroundColor: COLOR.green50, 
                        borderRadius: '4px',
                        fontSize: '14px',
                        color: COLOR.black200
                    }}>
                        <span style={{ fontWeight: 600 }}>
                            {teacherClasses[0].displayName}
                        </span>
                        <span> has </span>
                        <span style={{ fontWeight: 600 }}>
                            {teacherClasses[0].no_of_student_have_submission}
                        </span>
                        <span> students with submissions</span>
                    </div>
                )}
                <div style={{ marginBottom: '16px', width: '100%' }}>
                    {/* <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                        Select Source Class:
                    </label>
                    {loadingClasses ? (
                        <Loading size={20} />
                    ) : (
                        <Select
                            value={selectedSourceClass || ''}
                            onChange={(e) => setSelectedSourceClass(e.target.value)}
                        >
                            <option value="">Select a class...</option>
                            {teacherClasses.map(cls => (
                                <option key={cls.id} value={cls.id}>
                                    {cls.displayName || cls.name || cls.id}
                                </option>
                            ))}
                        </Select>
                    )} */}
                    {teacherClasses.length === 0 && !loadingClasses && (
                        <div style={{ marginTop: '8px', color: COLOR.gray100, fontSize: '12px' }}>
                            No other classes available
                        </div>
                    )}
                </div>
                {syncStatus.isSynced && syncStatus.syncedClasses.length > 0 && (
                    <SyncedClassesDisplay 
                        syncedClasses={syncStatus.syncedClasses} 
                        onUndoClick={handleUndoClick}
                    />
                )}
                <Dialog
                    open={undoDialogOpen}
                    onClose={handleUndoCancel}
                >
                    <DialogTitle>Confirm Undo</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to undo all synced data? This action will remove all data that was brought from other sections. This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleUndoCancel} disabled={undoLoading}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleUndoConfirm} 
                            disabled={undoLoading}
                            style={{
                                backgroundColor: COLOR.red || '#d32f2f',
                                color: COLOR.white,
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '3px',
                                cursor: undoLoading ? 'not-allowed' : 'pointer',
                                opacity: undoLoading ? 0.6 : 1
                            }}
                        >
                            {undoLoading ? <Loading size={15} white /> : 'Confirm Undo'}
                        </Button>
                    </DialogActions>
                </Dialog>
                <ModalRow>
                    <Button
                        style={{ marginRight: '4px' }}
                        onClick={onClose}
                        disabled={bringDataLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleBringData}
                        disabled={!selectedSourceClass || bringDataLoading}
                        style={{
                            backgroundColor: COLOR.green100,
                            color: COLOR.white,
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '3px',
                            cursor: bringDataLoading || !selectedSourceClass ? 'not-allowed' : 'pointer',
                            opacity: bringDataLoading || !selectedSourceClass ? 0.6 : 1
                        }}
                    >
                        {bringDataLoading ? <Loading size={15} white /> : 'Bring Data'}
                    </Button>
                </ModalRow>
            </ModalContent>
        </Modal>
    );
};

export default BringDataFromSectionModal;
